import { useMemo } from 'react'
import {
  useQueryParam,
  serializeQueryParams,
} from '~publish/hooks/useQueryParam'

export interface PostFilterParams {
  channels?: string[]
  tags?: string[]
  status?: string
  date?: string
  filter_preset?: string
}

export interface UsePostFilterParamsReturn {
  /**
   * Current filter parameters from URL
   */
  filters: PostFilterParams

  /**
   * Serialize current filters to query string with proper bracket formatting
   * @returns Query string with current filters (e.g., "?channels[]=id1&channels[]=id2&tags[]=tag1")
   */
  queryParams: string
}

/**
 * Hook to manage post filter parameters in URL query string.
 * Provides read access to current filters and utilities to serialize them for navigation.
 *
 * This hook reads filter parameters from the current URL and provides a serialized
 * query string that can be used to preserve filters when navigating between routes.
 *
 * @returns {UsePostFilterParamsReturn} Object containing:
 *   - `filters`: Current filter values from URL query parameters
 *   - `queryParams`: Serialized query string with current filters (includes leading '?' if filters exist)
 *
 * @remarks
 * - Only includes filters that have values in the serialized output
 * - Empty arrays are treated as "no value" and excluded from serialization
 * - Array parameters (channels, tags) use bracket notation (e.g., "channels[]=id1&channels[]=id2")
 * - String parameters (status, date, filter_preset) are included as-is
 * - Returns empty string for queryParams when no filters are active
 *
 * @example
 * ```tsx
 * const { filters, queryParams } = usePostFilterParams()
 *
 * // Navigate preserving current filters
 * navigate(`/some-route${queryParams}`)
 *
 * // Check if filters are active
 * const hasFilters = filters.channels?.length || filters.tags?.length
 *
 * // Example outputs:
 * // No filters: queryParams = ""
 * // With filters: queryParams = "?channels[]=ch1&tags[]=tag1&status=draft"
 * ```
 */
export function usePostFilterParams(): UsePostFilterParamsReturn {
  const [channels] = useQueryParam<string[]>('channels')
  const [tags] = useQueryParam<string[]>('tags')
  const [status] = useQueryParam<string>('status')
  const [date] = useQueryParam<string>('date')
  const [filterPreset] = useQueryParam<string>('filter_preset')

  const filters: PostFilterParams = {
    channels,
    tags,
    status,
    date,
    filter_preset: filterPreset,
  }

  const queryParams = useMemo((): string => {
    const filterParams: Record<string, string | string[]> = {}

    // Only include filters that have values
    if (channels?.length) {
      filterParams.channels = channels
    }

    if (tags?.length) {
      filterParams.tags = tags
    }

    if (status) {
      filterParams.status = status
    }

    if (date) {
      filterParams.date = date
    }

    if (filterPreset) {
      filterParams.filter_preset = filterPreset
    }

    const queryString = serializeQueryParams(filterParams)
    return queryString ? `?${queryString}` : ''
  }, [channels, tags, status, date, filterPreset])

  return {
    filters,
    queryParams,
  }
}
